import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./gen";

export class ApiError extends Error {
	status: number;
	details: unknown;

	constructor(status: number, details: unknown) {
		super(`API Error: ${status}`);
		this.name = "ApiError";
		this.status = status;
		this.details = details;
	}
}

const debug_auth_token: string | undefined = import.meta.env
	.VITE_APP_OWNER_AUTH;

const base_url: string =
	import.meta.env.VITE_APP_API_URL ??
	(() => {
		throw Error("uanble to create client without base_url");
	})();

// basic middleware for injecting bearer token
const auth: Middleware = {
	async onRequest({ request, options }) {
		if (debug_auth_token) {
			request.headers.set("Authorization", `Bearer ${debug_auth_token}`);
		}

		return request;
	},
};

// client that uses bearer_token or cookie for authentication
const authClient = createClient<paths>({
	baseUrl: base_url,
	credentials: "include",
});

authClient.use(auth);

// client that carries no auth info
const simpleClient = createClient<paths>({
	baseUrl: base_url,
});

export async function get_event(
	id: string,
): Promise<components["schemas"]["Event"]> {
	const { data, error, response } = await authClient.GET("/events/{event_id}", {
		params: {
			path: {
				event_id: id,
			},
		},
		parseAs: "json",
	});

	if (error) {
		throw new ApiError((response as Response).status, error);
	}
	return data;
}

export async function get_events(
	query_param: components["schemas"]["ListEventQueryParam"],
): Promise<components["schemas"]["ListEventPaginationResponse"]> {
	const { data, error, response } = await authClient.GET("/events", {
		params: {
			query: query_param,
		},
		parseAs: "json",
	});

	if (error) {
		throw new ApiError((response as Response).status, error);
	}
	return data;
}

export async function create_event(
	payload: components["schemas"]["CreateEventPayload"],
): Promise<string> {
	// INFO: non-json response sometimes won't return correctly
	const { data, error, response } = await authClient.POST("/events", {
		body: payload,
		parseAs: "text",
	});

	if (error) {
		throw new ApiError((response as Response).status, error);
	}
	return data;
}

export async function get_agents(): Promise<
	components["schemas"]["AgentStatus"][]
> {
	const { data, error, response } = await authClient.GET("/agents", {
		parseAs: "json",
	});

	if (error) {
		throw new ApiError((response as Response).status, error);
	}
	return data;
}

export async function create_agent(
	payload: components["schemas"]["CreateAgentRequest"],
): Promise<components["schemas"]["CreateAgentResponse"]> {
	const { data, error, response } = await authClient.POST("/agents", {
		body: payload,
		parseAs: "json",
	});

	if (!error) {
		return data;
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function create_agent_key(
	agent_profile_id: string,
): Promise<string> {
	const { data, error, response } = await authClient.POST(
		"/agents/{agent_profile_id}/key",
		{
			params: {
				path: {
					agent_profile_id: agent_profile_id,
				},
			},
			parseAs: "text",
		},
	);

	if (!error) {
		return data;
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function upload_agent_calibration(
	agentProfileId: string,
	file: File,
): Promise<void> {
	const buffer = await file.arrayBuffer();
	const bytes = Array.from(new Uint8Array(buffer));

	const { error, response } = await authClient.PUT(
		"/agents/{agent_profile_id}/calibration",
		{
			params: {
				path: {
					agent_profile_id: agentProfileId,
				},
			},
			body: {
				calibration: bytes,
			} as any,
			bodySerializer: () => {
				const formData = new FormData();
				formData.append("calibration", file);
				return formData;
			},
		},
	);

	if (error) {
		throw new ApiError((response as Response).status, error);
	}
}

export async function book_event(id: string): Promise<void> {
	const { data, error, response } = await authClient.PUT(
		"/events/{event_id}/book",
		{
			params: {
				path: {
					event_id: id,
				},
			},
			parseAs: "stream",
		},
	);

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function check_in_event(id: string): Promise<void> {
	const { data, error, response } = await authClient.PUT(
		"/events/{event_id}/check_in",
		{
			params: {
				path: {
					event_id: id,
				},
			},
			parseAs: "stream",
		},
	);

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function join_event(id: string): Promise<void> {
	const { data, error, response } = await authClient.POST(
		"/events/{event_id}/join",
		{
			params: {
				path: {
					event_id: id,
				},
			},
			parseAs: "stream",
		},
	);

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function append_sessions(
	id: string,
	payload: components["schemas"]["AppendSessionPayload"],
): Promise<void> {
	const { data, error, response } = await authClient.POST(
		"/events/{event_id}/sessions",
		{
			params: {
				path: {
					event_id: id,
				},
			},
			body: payload,
			parseAs: "stream",
		},
	);

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function create_checkin_claim(id: string): Promise<string> {
	const { data, error, response } = await authClient.POST(
		"/events/{event_id}/claims/check_in",
		{
			params: {
				path: {
					event_id: id,
				},
			},
			parseAs: "text",
		},
	);

	if (!error) {
		return data;
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function create_append_sessions_claim(
	id: string,
): Promise<string> {
	const { data, error, response } = await authClient.POST(
		"/events/{event_id}/claims/append_sessions",
		{
			params: {
				path: {
					event_id: id,
				},
			},
			parseAs: "text",
		},
	);

	if (!error) {
		return data;
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function logout(): Promise<void> {
	const { data, error, response } = await authClient.POST("/logout", {
		parseAs: "stream",
	});

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function whoami(): Promise<
	components["schemas"]["WhoamiPayload"]
> {
	const { data, error, response } = await authClient.GET("/whoami");

	if (!error) {
		return data;
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function get_event_with_claim(
	id: string,
	token: string,
): Promise<components["schemas"]["Event"]> {
	const { data, error, response } = await simpleClient.GET(
		"/events/{event_id}/token_claim/info",
		{
			params: {
				path: {
					event_id: id,
				},
				query: {
					token: token,
				},
			},
			parseAs: "json",
		},
	);

	if (!error) {
		return data;
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function check_in_with_claim(
	id: string,
	token: string,
): Promise<void> {
	const { data, error, response } = await simpleClient.POST(
		"/events/{event_id}/token_claim/check_in",
		{
			params: {
				path: {
					event_id: id,
				},
				query: {
					token: token,
				},
			},
			parseAs: "json",
		},
	);

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}

export async function append_session_with_claim(
	id: string,
	token: string,
	param: components["schemas"]["AppendSessionPayload"],
): Promise<void> {
	const { data, error, response } = await simpleClient.POST(
		"/events/{event_id}/token_claim/sessions",
		{
			params: {
				path: {
					event_id: id,
				},
				query: {
					token: token,
				},
			},
			body: param,
			parseAs: "stream",
		},
	);

	if (!error) {
	} else {
		throw new ApiError((response as Response).status, error);
	}
}
